/**
 * Utility functions for handling job start time formatting
 */

/**
 * Formats a date object or date string to ISO format with seconds and milliseconds set to 00
 * @param dateInput - Date object or date string to format
 * @returns ISO string with seconds and milliseconds set to 00 (YYYY-MM-DDTHH:MM:00.000Z)
 */
export const formatJobStartTime = (dateInput: Date | string | null): string | null => {
  if (!dateInput) {
    return null;
  }

  try {
    // Convert to Date object if it's a string
    const dateObj = dateInput instanceof Date ? dateInput : new Date(dateInput);

    // Format date with seconds and milliseconds set to 00
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');

    // Set seconds and milliseconds to 00
    const seconds = '00';
    const ms = '000';

    // Create ISO string with seconds and ms as 00
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}Z`;
  } catch (error) {
    return null;
  }
};

/**
 * Displays an ISO date string without timezone conversion
 * @param isoString - ISO date string from database (like "2025-05-16T14:30:00.000Z")
 * @returns Formatted time string without timezone conversion (like "May 16, 2025, 2:30 PM")
 */
export const displayISODateWithoutTimezoneConversion = (isoString: string | null): string => {
  if (!isoString) {
    return '';
  }

  try {
    // Parse the ISO string without timezone conversion
    const parts = isoString.split('T');
    const datePart = parts[0];
    const timePart = parts[1]?.split('.')[0];

    if (!datePart || !timePart) {
      return isoString;
    }

    // Parse date parts (YYYY-MM-DD)
    const [year, month, day] = datePart.split('-');

    // Parse time parts (HH:MM:SS)
    const [hours24, minutes] = timePart.split(':');

    // Convert 24-hour time to 12-hour format with AM/PM
    const hours24Num = parseInt(hours24);
    const hours12 = hours24Num % 12 || 12; // Convert 0 to 12
    const ampm = hours24Num >= 12 ? 'PM' : 'AM';

    // Format without converting timezone
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    const monthName = months[parseInt(month) - 1];

    // Return formatted string with 12-hour time
    return `${monthName} ${parseInt(day)}, ${year}, ${hours12}:${minutes} ${ampm}`;
  } catch (error) {
    return isoString || '';
  }
};

/**
 * Validates if a string is in proper ISO 8601 format
 * @param dateString - String to validate
 * @returns boolean indicating if the string is a valid ISO format
 */
const isValidISOString = (dateString: string): boolean => {
  // ISO 8601 format regex: YYYY-MM-DDTHH:mm:ss.sssZ or YYYY-MM-DDTHH:mm:ssZ or YYYY-MM-DD
  const isoRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;

  if (!isoRegex.test(dateString)) {
    return false;
  }

  // Additional validation: try to create a Date object and check if it's valid
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Formats an ISO date string to MM-DD-YYYY format
 * @param isoString - ISO date string (like "2025-05-16T14:30:00.000Z")
 * @returns Formatted date string in MM-DD-YYYY format (like "05-16-2025")
 */
export const formatDateToMMDDYYYY = (isoString: string | null): string => {
  if (!isoString) {
    return '';
  }

  // Check if the string is in proper ISO format
  if (!isValidISOString(isoString)) {
    return isoString; // Return as-is if not in ISO format
  }

  try {
    // Parse the ISO string manually to avoid timezone conversion
    const parts = isoString.split('T');
    const datePart = parts[0];

    if (!datePart) {
      return isoString;
    }

    // Parse date parts (YYYY-MM-DD)
    const [year, month, day] = datePart.split('-');

    if (!year || !month || !day) {
      return isoString;
    }

    // Format as MM-DD-YYYY
    return `${month}-${day}-${year}`;
  } catch (error) {
    return isoString || '';
  }
};

/**
 * Parses an ISO string to a Date object without timezone conversion
 * @param isoString - ISO date string (like "2025-05-16T14:30:00.000Z")
 * @returns Date object with the exact time from the ISO string (no timezone conversion)
 */
export const parseISOStringToDateWithoutTimezoneConversion = (isoString: string): Date | null => {
  if (!isoString) {
    return null;
  }

  try {
    // Parse the ISO string manually to avoid timezone conversion
    const parts = isoString.split('T');
    const datePart = parts[0];
    const timePart = parts[1]?.split('.')[0] || '00:00:00';

    if (!datePart) {
      return null;
    }

    // Parse date parts (YYYY-MM-DD)
    const [yearStr, monthStr, dayStr] = datePart.split('-');
    const year = parseInt(yearStr);
    const month = parseInt(monthStr) - 1; // JavaScript months are 0-indexed
    const day = parseInt(dayStr);

    // Parse time parts (HH:MM:SS)
    const [hoursStr, minutesStr, secondsStr = '0'] = timePart.split(':');
    const hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);
    const seconds = parseInt(secondsStr);

    // Create a new date and set local time components to match the parsed UTC values
    const date = new Date();

    // Set local time components without any timezone adjustments
    date.setFullYear(year);
    date.setMonth(month);
    date.setDate(day);
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(seconds);
    date.setMilliseconds(0);

    return date;
  } catch (error) {
    return null;
  }
};
